import React from 'react';
import { fmtDate } from '../lib/dates';
import { formatList } from '../lib/listFormat';

// Profile tab inside the supplier-portal slide-out. Pure read-only –
// supplier can't edit their own record (changes go via Help & Contact).
//
// BRIEF-38 Part B: reframed as view-only "on file". Instead of a wall of
// "Not provided" rows, each section is a bordered card that shows only the
// populated fields; the empties collapse into a single quiet dashed prompt
// ("… aren't on file yet – ask Fusion to add them"), so gaps read as
// "to complete via Fusion", not "neglected". An entirely-empty section
// becomes one prompt. `user` is the signed-in supplier_user (BRIEF-38 B4).

const isEmpty = (v) => v == null || v === '';

// One compact field row inside a section card. Stacks label-above-value on
// mobile (§8); side-by-side from sm up.
const Row = ({ label, value, mono = false }) => (
  <div className="grid grid-cols-1 sm:grid-cols-[128px_1fr] gap-1 sm:gap-3 py-2 border-b border-line last:border-b-0 sm:items-baseline">
    <div className="font-['Metropolis'] text-[10px] font-bold tracking-[0.16em] uppercase text-[#51534A]">
      {label}
    </div>
    <div className={`text-[13px] text-ink font-semibold break-words ${mono ? "font-['JetBrains_Mono','ui-monospace',monospace]" : ''}`}>
      {value}
    </div>
  </div>
);

// Section eyebrow – deep-green (kept, B5).
const SectionLabel = ({ children }) => (
  <div className="font-['Metropolis'] text-[10px] font-bold tracking-[0.25em] uppercase text-[#009159] mb-2.5">
    {children}
  </div>
);

// The "Ask Fusion to add it / them →" link. `plural` picks the pronoun so it
// stays correct whether one field or several are being asked for.
const AskLink = ({ onAsk, plural }) => (
  <button type="button" onClick={onAsk} className="text-[#2E8FA1] font-bold hover:underline bg-transparent border-0 p-0 cursor-pointer font-['Metropolis']">
    Ask Fusion to add {plural ? 'them' : 'it'} →
  </button>
);

// A bordered section card. `fields` = [{ label, value, mono, prose }].
// Populated fields render as rows; empties collapse into one dashed prompt. If
// nothing is populated, the whole section becomes the `emptyLabel` prompt.
//
// `prose` is the field's name as it should read mid-sentence in the missing
// list (e.g. "company number", "VAT status") – the display `label` is title
// case and abbreviated ("Company No.") so it can't be reused verbatim. The list
// is joined with formatList so it's correct at one, two, three or more items;
// the verb and the ask-pronoun follow the count. `emptyAskPlural` sets the
// pronoun for the whole-section-empty prompt, where the subject is the section
// concept (an address → "it"; business details → "them"), not the field count.
const SectionCard = ({ label, fields, emptyLabel, emptyAskPlural = false, onAsk }) => {
  const populated = fields.filter(f => !isEmpty(f.value));
  const missing = fields.filter(f => isEmpty(f.value));
  const missingProse = missing.map(f => f.prose || f.label);
  const one = missing.length === 1;
  return (
    <div className="mb-5">
      <SectionLabel>{label}</SectionLabel>
      {populated.length === 0 ? (
        <div className="rounded-xl border border-dashed border-[#CDD4BC] bg-[#F7F9F0] px-4 py-3.5">
          <p className="font-['Metropolis'] text-[12px] text-[#7C7F6F] leading-relaxed">
            {emptyLabel} <AskLink onAsk={onAsk} plural={emptyAskPlural} />
          </p>
        </div>
      ) : (
        <>
          <div className="bg-white border border-[#E6EADB] rounded-xl px-4">
            {populated.map(f => <Row key={f.label} label={f.label} value={f.value} mono={f.mono} />)}
          </div>
          {missing.length > 0 && (
            <div className="mt-2 rounded-lg border border-dashed border-[#CDD4BC] bg-[#F7F9F0] px-3.5 py-2.5">
              <p className="font-['Metropolis'] text-[12px] text-[#7C7F6F] leading-relaxed">
                {one ? 'One detail isn' : 'Some details aren'}&rsquo;t on file yet – {formatList(missingProse)} {one ? 'is' : 'are'} missing. <AskLink onAsk={onAsk} plural={!one} />
              </p>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default function SupplierProfileTab({ supplier, user, onSwitchToHelpTab }) {
  if (!supplier) {
    return (
      <p className="font-['Metropolis'] text-sm text-[#51534A] italic py-4">Loading your details…</p>
    );
  }

  const businessFields = [
    { label: 'Business Name', prose: 'business name', value: supplier.supplier_name },
    { label: 'Trading As', prose: 'trading name', value: supplier.trading_as || supplier.supplier_name },
    { label: 'Company No.', prose: 'company number', value: supplier.company_number, mono: true },
    { label: 'VAT Status', prose: 'VAT status', value: supplier.vat_status },
    { label: 'Category', prose: 'category', value: [supplier.category, supplier.sub_category].filter(Boolean).join(' · ') || null },
    { label: 'Member Since', prose: 'member-since date', value: fmtDate(supplier.created_at) },
  ];

  // Primary Contact = the signed-in supplier_user (name + email only; the
  // record has no per-user role/phone). Fixes the header/profile email
  // mismatch (BRIEF-38 B4).
  const contactFields = [
    { label: 'Name', prose: 'name', value: user?.full_name || supplier.main_contact_name },
    { label: 'Email', prose: 'email', value: user?.email || supplier.main_contact_email },
  ];

  const streetLines = [supplier.address_line_1, supplier.address_line_2, supplier.address_line_3].filter(Boolean);
  const addressFields = [
    { label: 'Street', prose: 'street', value: streetLines.length > 0 ? streetLines.join(', ') : null },
    { label: 'City', prose: 'city', value: supplier.city },
    { label: 'Postcode', prose: 'postcode', value: supplier.postcode, mono: true },
    { label: 'Region', prose: 'region', value: supplier.region },
  ];

  return (
    <div className="font-['Metropolis']">

      {/* View-only callout (B1) – reframed as "on file", teal info token. */}
      <div className="bg-[#eaf3ef] border border-[#2E8FA1]/30 rounded-md px-3.5 py-3 flex gap-2.5 mb-5">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#2E8FA1" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className="flex-shrink-0 mt-0.5">
          <circle cx="12" cy="12" r="10" />
          <line x1="12" y1="16" x2="12" y2="12" />
          <line x1="12" y1="8" x2="12.01" y2="8" />
        </svg>
        <div className="text-[11px] leading-relaxed text-fusion-mid-grey">
          <strong className="text-ink">This is what Fusion 2K holds on file for you.</strong> To update anything, message the team from the{' '}
          <button
            type="button"
            onClick={onSwitchToHelpTab}
            className="text-[#2E8FA1] font-bold hover:underline cursor-pointer bg-transparent border-0 p-0 font-['Metropolis']">
            Help &amp; contact
          </button>
          {' '}tab and we&rsquo;ll update it for you.
        </div>
      </div>

      {/* emptyAskPlural: "business details" is plural → "them"; a single
          contact or address → "it". */}
      <SectionCard label="Business" fields={businessFields} emptyLabel="No business details on file yet." emptyAskPlural onAsk={onSwitchToHelpTab} />
      <SectionCard label="Primary Contact" fields={contactFields} emptyLabel="No contact on file yet." onAsk={onSwitchToHelpTab} />
      <SectionCard label="Address" fields={addressFields} emptyLabel="No address on file yet." onAsk={onSwitchToHelpTab} />
    </div>
  );
}
